import { formatDistanceToNow } from "date-fns";
import { LogOut } from "lucide-react";
import type { JSX } from "react";
import { useNavigate } from "react-router";

import { ConfirmDialog, DataTable, ErrorState } from "../../components/data/index.js";
import { Badge } from "../../components/ui/badge.js";
import { Button } from "../../components/ui/button.js";
import {
  useLogoutAllMutation,
  useRevokeSessionMutation,
  useSessionsQuery,
  type SessionInfo,
} from "../../lib/api/auth.js";
import { toast } from "../../lib/toast.js";
import { parseUserAgent } from "../../lib/user-agent.js";

export const handle = { crumb: "Sessions" };

function formatRelative(iso: string): string {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return iso;
  }
}

export function SessionsPage(): JSX.Element {
  const query = useSessionsQuery();
  const revoke = useRevokeSessionMutation();
  const logoutAll = useLogoutAllMutation();
  const navigate = useNavigate();

  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;

  const columns = [
    {
      key: "device",
      header: "Device",
      cell: (r: SessionInfo) => (
        <div className="flex items-center gap-2">
          <span>{parseUserAgent(r.userAgent)}</span>
          {r.isCurrent ? <Badge variant="secondary">This device</Badge> : null}
        </div>
      ),
    },
    {
      key: "ip",
      header: "IP",
      cell: (r: SessionInfo) => (
        <span className="font-mono text-xs">{r.ipAddress ?? "—"}</span>
      ),
    },
    { key: "last", header: "Last seen", cell: (r: SessionInfo) => formatRelative(r.lastSeenAt) },
    { key: "created", header: "Signed in", cell: (r: SessionInfo) => formatRelative(r.createdAt) },
    {
      key: "actions",
      header: "",
      cell: (r: SessionInfo) =>
        r.isCurrent ? (
          <span className="text-xs text-muted-foreground">—</span>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              revoke.mutate(r.id, {
                onSuccess: () => toast.success("Session revoked"),
              });
            }}
            disabled={revoke.isPending}
          >
            Revoke
          </Button>
        ),
      className: "w-24",
    },
  ];

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Active sessions</h1>
          <p className="text-sm text-muted-foreground">
            Devices currently signed into your account.
          </p>
        </div>
        <ConfirmDialog
          trigger={
            <Button variant="outline" size="sm">
              <LogOut className="mr-2 h-4 w-4" />
              Log out everywhere
            </Button>
          }
          title="Log out from every device?"
          description="You'll be signed out from all sessions including this one and returned to the sign-in page."
          confirmLabel="Log out everywhere"
          destructive
          onConfirm={async () => {
            await logoutAll.mutateAsync();
            void navigate("/login");
          }}
        />
      </header>
      <DataTable
        rowKey={(r) => r.id}
        rows={query.data?.items}
        loading={query.isLoading}
        columns={columns}
        emptyTitle="No active sessions"
      />
    </div>
  );
}
